import { makeGetRouteForLastSelectedTab } from './factories'
import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import type { TEntityId } from '~/types/models'
import type {
  IOrder,
  IOrderOverview,
  IOrderLastSelectedState,
  IOverviewForPost,
} from '~/types/order/models'

const apiEntityPath = '/api/orders'

export async function getOrders({ apiPath = apiEntityPath } = {}) {
  const callingMn = getOrders.name
  return httpGet<IOrder[]>({ callingMn, apiPath })
}

export async function getOrder(id: string) {
  const callingMn = getOrder.name
  const apiPath = `${apiEntityPath}/${id}`
  return httpGet<IOrder>({ callingMn, apiPath })
}

/**
 * Put every group's msServices in hrid order, in place.
 *
 * Exported because an order overview arrives through TWO endpoints — this
 * silo's `/api/orders/:id/overview` and the opportunity silo's
 * `/api/opportunities/:id/orderoverview`, which the Opportunity Overview's
 * Operations tab reads — and the card set assumes the order either way. Shared
 * rather than copied so the second caller cannot quietly lose the sort.
 */
export function sortOrderMSServicesByHrid(overview: IOrderOverview) {
  overview.orderMasterServices.forEach((moms) =>
    moms.orderMSServices?.sort((a, b) => a.hrid.localeCompare(b.hrid))
  )
  return overview
}

export async function getOrderOverview(id: string) {
  const callingMn = getOrderOverview.name
  const apiPath = `${apiEntityPath}/${id}/overview`
  const overview = await httpGet<IOrderOverview>({ callingMn, apiPath })
  return sortOrderMSServicesByHrid(overview)
}

export async function getLastSelectedStateForOrder(id: TEntityId) {
  const callingMn = getLastSelectedStateForOrder.name
  const apiPath = `${apiEntityPath}/${id}/lastselectedstate`
  return httpGet<IOrder>({ callingMn, apiPath })
}

export async function getSuppliersFromOrder(
  id: TEntityId,
  onlyWithReadyForContractMSServices: boolean = false
) {
  const callingMn = getSuppliersFromOrder.name
  const apiPath =
    `${apiEntityPath}/${id}/suppliers` +
    (onlyWithReadyForContractMSServices
      ? '?onlywithreadyforcontractmsservices=true'
      : '')
  return httpGet<IOrder>({ callingMn, apiPath })
}

export const getRouteForLastSelectedTab =
  makeGetRouteForLastSelectedTab<IOrderLastSelectedState>(
    apiEntityPath,
    'Orders-Overview'
  )

export function getRouteForOnCreated(id: string) {
  return { name: 'Orders-Overview-Sales-id', params: { id } }
}

export async function createOrder(entity: IOrder) {
  const callingMn = createOrder.name
  const apiPath = apiEntityPath
  return httpPost<IOrder>({ callingMn, apiPath, entity })
}

export async function createOrderOverview(entity: IOverviewForPost) {
  const callingMn = createOrderOverview.name
  const apiPath = `${apiEntityPath}/overview`
  return httpPost<IOverviewForPost>({
    callingMn,
    apiPath,
    entity,
    asyncOptions: { doAsync: true },
  })
}

export async function updateOrder(entityData: Partial<IOrder>, id: TEntityId) {
  const callingMn = updateOrder.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialIOrder = Partial<IOrder>
  return httpPatchMerge<TPartialIOrder>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteOrder(entityData: IOrder) {
  const callingMn = deleteOrder.name
  const apiPath = apiEntityPath
  return httpDelete<IOrder>({
    callingMn,
    apiPath,
    entityData,
  })
}
